'use client'

import React, { Component, ErrorInfo, ReactNode } from 'react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    }
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // 에러 로깅 서비스로 전송 (예: Sentry)
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    
    // 로컬 스토리지에 에러 정보 저장
    if (typeof window !== 'undefined') {
      const errorLog = {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString()
      }
      
      const existingErrors = localStorage.getItem('errorLogs')
      const errors = existingErrors ? JSON.parse(existingErrors) : []
      errors.push(errorLog)
      
      // 최대 10개의 에러만 저장
      if (errors.length > 10) {
        errors.shift()
      }
      
      localStorage.setItem('errorLogs', JSON.stringify(errors))
    }
    
    this.setState({
      errorInfo
    })
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    })
  }

  render() {
    if (this.state.hasError) {
      // 커스텀 fallback이 제공되면 사용
      if (this.props.fallback) {
        return <>{this.props.fallback}</>
      }

      // 기본 에러 UI
      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 flex items-center justify-center p-4">
          <div className="max-w-md w-full">
            <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-8 shadow-2xl">
              {/* 에러 아이콘 */}
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center">
                  <svg className="w-10 h-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
              </div>

              {/* 에러 메시지 */}
              <h1 className="text-2xl font-bold text-white text-center mb-3">
                문제가 발생했습니다
              </h1>
              
              <p className="text-gray-400 text-center mb-6">
                예상치 못한 오류가 발생했습니다.<br />
                잠시 후 다시 시도해주세요.
              </p>

              {/* 에러 상세 (개발 환경에서만) */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mb-6">
                  <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-400">
                    에러 상세 보기
                  </summary>
                  <pre className="mt-2 p-3 bg-black/50 rounded-lg text-xs text-red-400 overflow-auto max-h-40">
                    {this.state.error.message}
                    {this.state.error.stack}
                  </pre>
                </details>
              )}

              {/* 액션 버튼 */}
              <div className="flex gap-3">
                <button
                  onClick={this.handleReset}
                  className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
                >
                  다시 시도
                </button>
                <button
                  onClick={() => window.location.href = '/'}
                  className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded-lg transition-colors"
                >
                  홈으로
                </button>
              </div>

              {/* 도움말 */}
              <div className="mt-6 pt-6 border-t border-slate-700/50">
                <p className="text-xs text-gray-500 text-center">
                  문제가 계속되면{' '}
                  <a href="mailto:support@todaydiving.com" className="text-blue-400 hover:text-blue-300">
                    support@todaydiving.com
                  </a>
                  {' '}으로 문의해주세요.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}